import { Experiment, ExperimentClient } from '@amplitude/experiment-js-client';

/**
 * Amplitude Experiment client.
 *
 * The deployment key is injected by the app rather than read from
 * `@hakimo-ui/hakimo/environments` here: that module evaluates `window._APP_ENV_`
 * at import time and throws when it is absent, which would break every test that
 * transitively imports this one. Keeping the key an argument also leaves this
 * lib free of environment coupling, as the rest of it is.
 *
 * With no key configured -- local, airgapped, or simply not set up yet -- every
 * flag reads as off rather than erroring.
 */
let deploymentKey: string | undefined;
let client: ExperimentClient | undefined;
let started: Promise<void> | undefined;
let fetchingFor: string | undefined;
let fetchedFor: string | undefined;
let refreshTimer: ReturnType<typeof setInterval> | undefined;

const listeners = new Set<() => void>();

/**
 * Operators keep Unified Monitoring open for a whole shift, so turning a flag
 * off has to reach a tab that never reloads.
 */
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/** Called once during app init. An empty or 'UNKNOWN' key disables flags. */
export function configureExperiment(key: string | undefined): void {
  deploymentKey = key && key !== 'UNKNOWN' ? key : undefined;
}

/**
 * Initialises the client and fetches variants for `userId`.
 *
 * Safe to call repeatedly: later calls await the same promise unless the user
 * changed, which refetches -- the SDK caches variants per deployment, not per
 * user, so a shared console would otherwise serve whoever logged in first. A
 * failure resolves rather than rejects: an unreachable flag service must not
 * stop the app rendering.
 */
export function startExperiment(userId: string): Promise<void> {
  if (!deploymentKey) {
    return Promise.resolve();
  }
  if (!client) {
    client = Experiment.initialize(deploymentKey, {
      automaticExposureTracking: true,
    });
  }
  if (!started || fetchingFor !== userId) {
    fetchingFor = userId;
    fetchedFor = undefined;
    started = fetchVariants(userId);
    clearInterval(refreshTimer);
    refreshTimer = setInterval(
      () => fetchVariants(userId),
      REFRESH_INTERVAL_MS
    );
  }
  return started;
}

function fetchVariants(userId: string): Promise<void> {
  return (client as ExperimentClient)
    .fetch({ user_id: userId })
    .then(
      () => {
        fetchedFor = userId;
      },
      () => {
        fetchedFor = undefined;
      }
    )
    .then(() => {
      listeners.forEach((notify) => notify());
    });
}

/**
 * Subscribe to variant refreshes. Returns the unsubscribe function.
 *
 * Without this a withdrawn flag would only take effect on the next reload,
 * which for a monitoring console can be days.
 */
export function onVariantsChanged(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The raw variant value for a flag, or undefined when unknown/unconfigured.
 * Prefer `useFeatureFlag` in components -- this exists for non-React callers.
 */
export function getVariant(flagKey: string): string | undefined {
  // The SDK loads variants from localStorage in its constructor, so without
  // this it would serve a withdrawn -- or another operator's -- value both
  // before the first fetch lands and after any fetch fails. Only a fetch that
  // succeeded for the user we are currently fetching for counts; anything else
  // falls back to off, which is what the flag promises.
  return fetchedFor && fetchedFor === fetchingFor
    ? client?.variant(flagKey)?.value
    : undefined;
}
